import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Spin, Tabs, Button, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import { PATH } from 'constants/path';
import s from './s.module.less';
import {
    EKlarityPage,
} from 'types/helloKlarity';
import {
    getHelloKlarityPageMedicationDetailById,
    saveHelloKlarityPageMedicationDetail,
} from 'api/operation';
import ContentMgmt from './components/ContentMgmt';

import { ServerMeta } from 'types/common';
import useLoadBasicData from 'hooks/useLoadBasicData';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import { getUrlParam, isDevEnv, isPrereleaseEnv, isProductionEnv, isTestEnv } from 'utils/common';
import MedicationMgmt from './components/MedicationMgmt';
import BannerMgmt from './components/BannerMgmt';
import TableMgmt from './components/TableMgmt';
import SafetyInfoMgmt from './components/SafetyInfoMgmt';
import FAQMgmt from './components/FAQMgmt';
import StickyBannerMgmt from './components/StickyBannerMgmt';
import WebPreview, { PreviewRef } from './components/Preview/Preview';

// 导入数据管理器
import {
    createDataManager,
    DataManager,
    addTempIdsToData,
    removeTempIdsFromData,
} from './utils/dataUpdater';
import dayjs from 'utils/dayjs';
import RightPanel from './components/Preview';

const Detail: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [allCondition] = useGetSettingData(EnumFields.HELLO_KLARITY_CONDITION);
    const [tabActiveKey, setTabActiveKey] = useState('1');
    const [fetched, setFetched] = useState(false);
    const [condition, setCondition] = useState<ServerMeta>();
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');

    // 添加preview ref
    const previewRef = useRef<PreviewRef>(null);

    // 添加上次发送的数据引用，避免重复发送相同数据
    const lastSentDataRef = useRef<string>('');

    // 添加iframe准备状态标记
    const [iframeReady, setIframeReady] = useState(false);

    const navigate = useNavigate();
    let { type } = useParams();
    const id = getUrlParam('id');
    const lastUpdateAt = getUrlParam('lastUpdateAt');
    type = type?.toLocaleUpperCase();

    const [loadBaiscData] = useLoadBasicData();

    useEffect(() => {
        if (condition) {
            let host = 'http://localhost:3010';

            if (isDevEnv()) {
                host = 'https://dev.d2ysjydukc4efk.amplifyapp.com';
            } else if (isTestEnv()) {
                host = 'https://test.d2ysjydukc4efk.amplifyapp.com';
            } else if (isProductionEnv() || isPrereleaseEnv()) {
                host = 'https://www.helloklarity.com';
            }

            const url = `${host}/medication-list/${condition.dictKey}`;
            setPreviewUrl(url);
        }
    }, [condition]);

    // 统一的数据管理器 - 初始为空数据
    const [dataManager] = useState<DataManager>(() => {
        return createDataManager(); // 不传入initialData，使用空数据
    });

    // 监听dataManager数据变化，发送到iframe
    const sendPreviewData = useCallback(() => {
        if (previewRef.current && isDataLoaded && iframeReady) {
            try {
                // 获取当前的完整数据（getMergedData内部会调用saveAllTabChanges）
                const currentData = dataManager.getMergedData();
                const dataString = JSON.stringify(currentData);

                // 避免重复发送相同数据
                if (dataString !== lastSentDataRef.current) {
                    lastSentDataRef.current = dataString;

                    // 将当前tab key转换为anchor
                    const anchor = parseInt(tabActiveKey, 10) || 1;

                    previewRef.current.sendPreviewData({
                        anchor,
                        data: currentData,
                    });
                }
            } catch (error) {
                console.error('Error sending preview data:', error);
            }
        }
    }, [tabActiveKey, isDataLoaded, iframeReady]);

    // 监听iframe的postMessage，确认iframe页面已准备就绪
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // 检查消息来源和类型
            if (event.data && event.data.type === 'iframeReady') {
                console.log('😁 Iframe is ready to receive data');
                setIframeReady(true);
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    // 当数据加载完成且iframe准备就绪后，发送初始数据
    useEffect(() => {
        if (isDataLoaded && iframeReady) {
            // 延迟发送，确保状态已更新
            setTimeout(sendPreviewData, 200);
        }
    }, [isDataLoaded, iframeReady, sendPreviewData]);

    // 创建一个可以被子组件调用的数据更新函数
    const triggerPreviewUpdate = useCallback(() => {
        setTimeout(sendPreviewData, 100); // 稍微延迟以确保数据已更新
    }, [sendPreviewData]);

    const commonProps = {
        condition,
        dataManager,
        onDataChange: triggerPreviewUpdate, // 传递数据变化回调
    };

    // 监听用户操作，检测数据变化
    useEffect(() => {
        if (isDataLoaded && iframeReady) {
            const handleUserInteraction = () => {
                setTimeout(sendPreviewData, 300); // 用户操作后延迟检查数据变化
            };

            // 监听多种用户操作事件
            document.addEventListener('click', handleUserInteraction);
            document.addEventListener('input', handleUserInteraction);
            document.addEventListener('change', handleUserInteraction);

            return () => {
                document.removeEventListener('click', handleUserInteraction);
                document.removeEventListener('input', handleUserInteraction);
                document.removeEventListener('change', handleUserInteraction);
            };
        }
    }, [isDataLoaded, iframeReady, sendPreviewData]);

    const tabItems = [
        {
            key: '1',
            label: <span className={s.tabLabel}>Content mgmt</span>,
            children: (
                <ContentMgmt
                    {...commonProps}
                    hkUrl={previewUrl}
                />
            ),
        },
        {
            key: '2',
            label: <span className={s.tabLabel}>Medication mgmt</span>,
            children: <MedicationMgmt {...commonProps} />,
        },
        {
            key: '3',
            label: <span className={s.tabLabel}>Banner</span>,
            children: <BannerMgmt {...commonProps} />,
        },
        {
            key: '4',
            label: <span className={s.tabLabel}>Table</span>,
            children: <TableMgmt {...commonProps} />,
        },
        {
            key: '5',
            label: <span className={s.tabLabel}>Safety info</span>,
            children: <SafetyInfoMgmt {...commonProps} />,
        },
        {
            key: '6',
            label: <span className={s.tabLabel}>FAQ mgmt</span>,
            children: <FAQMgmt {...commonProps} />,
        },
        {
            key: '7',
            label: <span className={s.tabLabel}>Sticky banner</span>,
            children: <StickyBannerMgmt {...commonProps} />,
        },
    ];

    const handleTabClick = useCallback((key: string) => {
        setTabActiveKey(key);

        // tab切换时立即发送数据，包含新的anchor
        setTimeout(() => {
            if (previewRef.current && isDataLoaded && iframeReady) {
                // 获取当前的完整数据（getMergedData内部会调用saveAllTabChanges）
                const currentData = dataManager.getMergedData();
                const anchor = parseInt(key, 10) || 1;

                previewRef.current.sendPreviewData({
                    anchor,
                    data: currentData,
                });
            }
        }, 100);
    }, [isDataLoaded, iframeReady]);

    const handleExit = useCallback(() => {
        navigate(`${PATH.OPERATION}/${PATH.HELLO_KLARITY_PAGE_MANAGEMENT}`, { replace: true });
    }, [navigate]);

    // 数据转换函数：将远程JSON数据转换为标准的MockData格式
    const transformApiData = (apiData: any) => {
        const transformed = { ...apiData };

        // 处理safetyInfoMgmt数据结构转换
        if (apiData.safetyInfoMgmt) {
            const safetyData = apiData.safetyInfoMgmt;

            // 检查是否是旧格式（sectionOneTitle等字段）
            if (safetyData.sectionOneTitle || safetyData.sectionOneSubtext ||
                safetyData.sectionTwoTitle || safetyData.sectionTwoSubtext) {
                // 转换为新格式
                transformed.safetyInfoMgmt = {
                    criticalSafetyInformation: safetyData.sectionOneSubtext || safetyData.sectionTwoSubtext || '',
                    generalSafetyConsiderations: [
                        safetyData.sectionOneTitle || '',
                        safetyData.sectionTwoTitle || '',
                    ].filter((item) => item.trim() !== ''), // 过滤空字符串
                };
            }
            // 如果已经是新格式，保持不变
            else if (safetyData.criticalSafetyInformation !== undefined ||
                safetyData.generalSafetyConsiderations !== undefined) {
                console.log('safetyInfoMgmt is already in correct format');
            }
            // 如果格式不明确，设置默认值
            else {
                transformed.safetyInfoMgmt = {
                    criticalSafetyInformation: '',
                    generalSafetyConsiderations: [],
                };
            }
        }

        return transformed;
    };

    const fetchData = async () => {
        if (id) {
            setIsLoading(true);
            try {
                const { data, error } = await getHelloKlarityPageMedicationDetailById(id);
                if (!error && data?.data) {
                    setFetched(true);

                    // 获取条件名称和设置
                    const conditionName = data.data.name;
                    const targetCondition = conditionName ? (allCondition as ServerMeta[])?.find((item) => item.dictKey.toLowerCase() === conditionName.toLowerCase() || item.content.toLowerCase() === conditionName.toLowerCase()) : undefined;

                    if (targetCondition) {
                        setCondition(targetCondition);
                    }

                    // 获取页面管理数据
                    try {
                        let managementData = null;

                        // 步骤1: 优先处理externalData中的newVersionLink获取远程JSON数据
                        if (data.data.externalData) {
                            try {
                                // 解析externalData JSON字符串
                                const externalDataObj = JSON.parse(data.data.externalData);

                                if (externalDataObj.newVersionLink) {
                                    // 远程获取JSON文件数据
                                    const response = await fetch(externalDataObj.newVersionLink);
                                    if (response.ok) {
                                        managementData = await response.json();
                                    } else {
                                        console.error('Failed to fetch data from newVersionLink:', response.status, response.statusText);
                                    }
                                }
                            } catch (parseError) {
                                console.error('Error parsing externalData:', parseError);
                            }
                        }

                        // 步骤2: 如果没有通过newVersionLink获取到数据，尝试从content字段获取
                        if (!managementData && data.data.content) {
                            managementData = typeof data.data.content === 'string'
                                ? JSON.parse(data.data.content)
                                : data.data.content;
                        }

                        // 步骤3: 将获取到的数据更新到DataManager
                        if (managementData) {
                            // 转换远程JSON数据以确保结构匹配
                            const transformedData = transformApiData(managementData);
                            // 添加临时ID到数据中，用于拖拽功能
                            const dataWithTempIds = addTempIdsToData(transformedData);
                            dataManager.updateInitialData(dataWithTempIds);
                            // setDataManager(createDataManager(dataWithTempIds));
                            setIsDataLoaded(true);
                        } else {
                            // 如果没有获取到具体数据，保持空数据结构
                            setIsDataLoaded(true);
                            console.log('No management data found, using empty structure');
                        }
                    } catch (dataError) {
                        console.error('Error processing management data:', dataError);
                        setIsDataLoaded(true); // 即使解析失败，也设置为已加载，使用空数据
                    }
                } else {
                    console.error('Failed to fetch data:', error);
                    setIsDataLoaded(true); // 即使失败，也设置为已加载，使用空数据
                }
            } catch (e) {
                console.error('Error in fetchData:', e);
                setIsDataLoaded(true); // 即使异常，也设置为已加载，使用空数据
            } finally {
                setIsLoading(false);
            }
        } else {
            // 没有ID，使用空数据结构
            setIsDataLoaded(true);
        }
    };

    const handleGoLive = async () => {
        if (!id) {
            message.error('Please enter your ID.');
            return;
        }
        try {
            // 获取包含所有修改的最终数据（getInitialData内部会调用saveAllTabChanges）
            const finalData = dataManager.getInitialData();

            // 移除临时ID，准备保存到服务器
            const dataToSave = removeTempIdsFromData(finalData);

            const { error } = await saveHelloKlarityPageMedicationDetail(id, dataToSave);
            if (!error) {
                message.success('Changes published successfully');
                fetchData();
            } else {
                message.error(error);
            }
        } catch (e) {
            console.log('e: ', e);
            message.error('Failed to save changes');
        }
    };

    useEffect(() => {
        loadBaiscData();
    }, [loadBaiscData]);

    useEffect(() => {
        if (type === EKlarityPage.FAQS) {
            setTabActiveKey('4');
        }
    }, [type]);

    useEffect(() => {
        if (allCondition && allCondition.length > 0 && !fetched && id && !isLoading) {
            fetchData();
        }
    }, [allCondition, id, isLoading, fetched]);

    const renderHeader = (
        <div className={s.titleWrap}>
            <h2 className={s.title}>Edit {condition?.content} listing page</h2>
            <h2 className={s.time}>Date last modified: {lastUpdateAt ? dayjs(lastUpdateAt).format('MMM DD, YYYY [at] hh:mm A') : ''}</h2>
            <div className={s.info}>
                <div className={s.left}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M8.25608 3.0976C9.02069 1.7383 10.9778 1.7383 11.7424 3.0976L17.3227 13.0181C18.0726 14.3514 17.1092 15.9987 15.5795 15.9987H4.41893C2.88927 15.9987 1.92585 14.3514 2.67578 13.0181L8.25608 3.0976ZM10.9991 12.9988C10.9991 13.5511 10.5514 13.9988 9.99915 13.9988C9.44686 13.9988 8.99915 13.5511 8.99915 12.9988C8.99915 12.4465 9.44686 11.9988 9.99915 11.9988C10.5514 11.9988 10.9991 12.4465 10.9991 12.9988ZM9.99915 4.99878C9.44686 4.99878 8.99915 5.44649 8.99915 5.99878V8.99878C8.99915 9.55106 9.44686 9.99878 9.99915 9.99878C10.5514 9.99878 10.9991 9.55106 10.9991 8.99878V5.99878C10.9991 5.44649 10.5514 4.99878 9.99915 4.99878Z" fill="#FBBF24" />
                    </svg>
                </div>
                <div className={s.right}>
                    <div className={s.title}>
                        Changes will be immediately saved
                    </div>
                    <div className={s.content}>
                        Please note that any changes you make on this page will be immediately saved and reflected on the website.
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPreview = (
        <div className={s.preview}>
            <RightPanel>
                <WebPreview ref={previewRef} link={previewUrl} />
                {/* 预览遮罩层 - 阻止交互但保留滚动 */}
                {/* <div
                    className={s.previewMask}
                /> */}
            </RightPanel>
        </div>
    );

    const renderFooter = (
        <div className={s.footer}>
            <Button type="primary" ghost onClick={handleExit}>
                Exit
            </Button>
            <Button type="primary" onClick={handleGoLive}>
                Go live
            </Button>
        </div>
    );

    return (
        <div className={s.wrap}>
            <Spin spinning={isLoading}>
                <div className={s.content}>
                    <div className={s.setting}>
                        {renderHeader}
                        {isDataLoaded && (
                            <Tabs
                                defaultActiveKey="1"
                                activeKey={tabActiveKey}
                                items={tabItems}
                                onTabClick={handleTabClick}
                            />
                        )}
                    </div>
                    {renderPreview}
                </div>
                {renderFooter}
            </Spin>
        </div>
    );
};

export default Detail;
